import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import Select from "react-select";
import dayjs from "dayjs";
import { Row, Col, Button, Card, CardBody } from "reactstrap";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useParams } from "react-router-dom";

const Docs = () => {
    const dispatch = useDispatch();
    const loading = useSelector(s => s.loading.loading);
    const { id } = useParams();
    const [customers, setCustomers] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [form, setForm] = useState({ customer_id: null, invoice_date: dayjs() });

    useEffect(() => {
        const loadData = async () => {
            const res = await Handler({ method: "get", url: "/customer/list.php?status=active&limit=1000" });
            if (res?.success) setCustomers(res.data.map(c => ({ value: c.id, label: c.company_name })));
        };
        loadData();
    }, []);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length + documents.length > 10) return toast.error("Max 10 images allowed");
        setDocuments([...documents, ...files]);
    };

    const removeFile = (idx) => {
        setDocuments(documents.filter((_, i) => i !== idx));
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (documents.length === 0) return toast.error("Please select at least one file");

        dispatch(loadingTrue());
        const formData = new FormData();
        formData.append("invoice_id", id);
        formData.append("date", dayjs(form.invoice_date).format("YYYY-MM-DD"));
        documents.forEach(file => formData.append("documents[]", file));

        const res = await Handler({
            method: "post", url: "/invoice/add_invoice_doc.php", data: formData,
            headers: { "Content-Type": "multipart/form-data" }
        });

        dispatch(loadingFalse());
        if (res?.success) {
            toast.success("Documents Uploaded Successfully!");
            setDocuments([]);
        } else toast.error(res?.message || "Upload failed");
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-card p-4 shadow-sm border-0 rounded-4 bg-white">
                <div className="mb-4">
                    <h4 className="fw-bold text-dark">Upload Supporting Documents</h4>
                    <p className="text-muted small">Select customer and attach relevant images or PDFs</p>
                </div>

                <form onSubmit={handleUpload}>
                    {/* MINI VIEW: BASIC DETAILS */}
                    <div className="mb-4 bg-light p-3 rounded-4 border-dashed">
                        <Row className="g-3">
                            <Col md={6}>
                                <label className="extra-small fw-bold text-muted text-uppercase">Customer</label>
                                <Select
                                    options={customers}
                                    placeholder="Select Customer..."
                                    onChange={val => setForm({ ...form, customer_id: val.value })}
                                />
                            </Col>
                            <Col md={6}>
                                <label className="extra-small fw-bold text-muted text-uppercase">Date</label>
                                <DatePicker
                                    slotProps={{ textField: { size: 'small', fullWidth: true, variant: 'outlined', sx: { bgcolor: 'white' } } }}
                                    value={form.invoice_date}
                                    onChange={val => setForm({ ...form, invoice_date: val })}
                                />
                            </Col>
                        </Row>
                    </div>

                    {/* MAIN SECTION: UPLOAD & PREVIEW */}
                    <Card className="border-0 shadow-sm rounded-4 bg-white">
                        <CardBody className="p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h6 className="fw-bold text-dark mb-0">
                                    <i className="bi bi-paperclip me-2 text-primary"></i>Attachments
                                    <span className="badge bg-light text-primary ms-2">{documents.length} / 10</span>
                                </h6>
                                <label className="btn btn-outline-primary btn-sm rounded-pill px-4 mb-0 cursor-pointer">
                                    <i className="bi bi-plus-lg me-2"></i> Add Files
                                    <input type="file" multiple accept="image/*,.pdf" className="d-none" onChange={handleFileChange} />
                                </label>
                            </div>

                            {documents.length === 0 ? (
                                <div className="text-center py-5 border-dashed rounded-4 bg-light bg-opacity-50">
                                    <i className="bi bi-cloud-arrow-up fs-1 text-muted opacity-25"></i>
                                    <p className="text-muted small mt-2">No files selected. Drag & drop or click 'Add Files'</p>
                                </div>
                            ) : (
                                <Row className="g-3">
                                    {documents.map((file, idx) => (
                                        <Col xs={6} sm={4} md={3} lg={2} key={idx}>
                                            <div className="position-relative border rounded-3 p-1 bg-white shadow-sm h-100">
                                                {file.type.startsWith("image/") ? (
                                                    <img
                                                        src={URL.createObjectURL(file)}
                                                        alt="preview"
                                                        className="rounded-2 w-100 object-fit-cover"
                                                        style={{ height: '120px' }}
                                                    />
                                                ) : (
                                                    <div className="d-flex align-items-center justify-content-center bg-danger bg-opacity-10 rounded-2" style={{ height: '120px' }}>
                                                        <i className="bi bi-file-pdf fs-1 text-danger"></i>
                                                    </div>
                                                )}
                                                <div className="p-2">
                                                    <div className="extra-small text-truncate fw-bold mb-1">{file.name}</div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm p-0 text-danger extra-small border-0 bg-transparent"
                                                        onClick={() => removeFile(idx)}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </CardBody>
                    </Card>

                    <div className="text-center mt-5">
                        <Button color="primary" size="lg" className="px-5 rounded-pill shadow-sm fw-bold border-0" disabled={loading || documents.length === 0} type="submit">
                            {loading ? "UPLOADING..." : "START UPLOAD"}
                        </Button>
                    </div>
                </form>
            </div>
        </LocalizationProvider>
    );
};

export default Docs;